import { useState, useEffect, useRef, useMemo } from "react"
import { Link, useNavigate } from "react-router-dom"
import { FaEdit, FaTrash, FaCheckCircle, FaFileExport, FaUndo, FaClipboardCheck } from "react-icons/fa";
import { toast } from 'react-toastify'
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Server from "../../Shared/Server"
import Spinner from '../../Shared/Spinner'
import { InputField } from "../../Shared/InputField";
import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import SelectField from "../../Shared/SelectField";
import AsyncSelectField from "../../Shared/AsyncSelect";

const Todos = () => {
    const [todos, setTodos] = useState([])
    const [errors, setErrors] = useState({})
    const [isLoading, setIsLoading] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [show, setShow] = useState(false);
    const [modalAction, setModalAction] = useState('delete');
    const [statusOptions] = useState([{id: 1, key: "Pending", name: "Pending"}, {id: 2, key: "Completed", name: "Completed"}, {id: 3, key: "Confirmed", name: "Confirmed"}])
    const [title, setTitle] = useState('')
    const [publicID, setPublicID] = useState('');
    const [categoryOptions, setCategoryOptions] = useState([]);
    const gridRef = useRef();
    const [formData, setFormData] = useState({
        fdate: new Date().toISOString().slice(0, 10),
        tdate: new Date().toISOString().slice(0, 10),
        status: "Pending",
        category_id: null
    })
    const {fdate, tdate, status, category_id} = formData

    const navigate = useNavigate();

    const loadOptions = async (url, query, setOptions) => {
        const params = { q: query };
        try {
            const response = await Server.get(`/options/${url}`, {params});
            setOptions(response.data);
            return response.data;
        } catch (error) {
            return [];
        }
    };

    useEffect(() => {
        const payload = {status, category_id}
        fetchData(payload)
        setTitle(`From ${fdate} To ${tdate} ${status} Todo list`)
    }, [])

    const dateFilterParams = {
        comparator: (filterDate, cellValue) => {
            if (!cellValue) return -1;
            const cellDate = new Date(cellValue);
            cellDate.setHours(0, 0, 0, 0);
            filterDate.setHours(0, 0, 0, 0);
            if (cellDate < filterDate) return -1;
            if (cellDate > filterDate) return 1;
            return 0;
        },
    }

    const columnDefs = useMemo(() => [
        {
            headerName: "#",
            filter: false,
            maxWidth: 70,
            sortable: true,
            valueFormatter: (params) => isNaN(params.node.id) ? "" : `${Number(params.node.rowIndex) + 1}`,
        },
        { headerName: "Date", field: "date", sortable: true, filter: 'agDateColumnFilter', filterParams: dateFilterParams },
        { headerName: "Category", field: "category", sortable: true, filter: true },
        { headerName: "Priority", field: "priority", sortable: true, filter: true },
        { headerName: "Location", field: "location_name", sortable: true, filter: true },
        { headerName: "Contact", field: "contact_number", sortable: true, filter: true },
        {
            headerName: "Actions",
            minWidth: 280,
            sortable: false,
            filter: false,
            pinned: 'left',
            lockPinned: true,
            cellRenderer: (e) => {
                if (!e.data) return null;
                const s = e.data.status;
                return (
                    <div className="d-flex gap-1">
                        <button className="btn btn-outline-success" onClick={() => handleShow(e.data.public_id, 'complete')} disabled={s !== 'Pending'} title="Complete"><FaCheckCircle/></button>
                        <button className="btn btn-outline-primary" onClick={() => handleShow(e.data.public_id, 'confirm')} disabled={s !== 'Completed'} title="Confirm"><FaClipboardCheck/></button>
                        <button className="btn btn-outline-warning" onClick={() => handleShow(e.data.public_id, 'undo')} disabled={s === 'Pending' || s === 'Confirmed'} title="Undo"><FaUndo/></button>
                        <Link className="btn btn-outline-info" to="/update-todo" state={{ todo: e.data }} title="Edit"><FaEdit/></Link>
                        <button className="btn btn-outline-danger" onClick={() => handleShow(e.data.public_id, 'delete')} disabled={s === 'Completed' || s === 'Confirmed'} title="Delete"><FaTrash/></button>
                    </div>
                )
            },
        },
        { headerName: "Technician", field: "technician_name", sortable: true, filter: true },
        { headerName: "Tech Contact", field: "technician_contact", sortable: true, filter: true },
        { headerName: "Status", field: "status", sortable: true, filter: true },
        { headerName: "User", field: "user", sortable: true, filter: true },
        { headerName: "Remark", field: "remark", sortable: true, filter: true },
        { headerName: "Village", field: "village", sortable: true, filter: true },
        { headerName: "Zone", field: "zone", sortable: true, filter: true },
        { headerName: "Meter Serial", field: "meter_serial", sortable: true, filter: true },
        { headerName: "Meter Reading", field: "meter_reading", sortable: true, filter: true },
        { headerName: "New Meter Serial", field: "new_meter_serial", sortable: true, filter: true },
        { headerName: "Due Date", field: "due_date", sortable: true, filter: 'agDateColumnFilter', filterParams: dateFilterParams },
        { headerName: "Completed At", field: "completed_at", sortable: true, filter: 'agDateColumnFilter', filterParams: dateFilterParams },
        { headerName: "Completed By", field: "completed_by_name", sortable: true, filter: true },
        { headerName: "Completion Notes", field: "completion_notes", sortable: true, filter: true },
        { headerName: "Failure Reason", field: "failure_reason", sortable: true, filter: true },
        { headerName: "GPS Lat", field: "gps_latitude", sortable: true, filter: true },
        { headerName: "GPS Lng", field: "gps_longitude", sortable: true, filter: true },
    ], []);

    const fetchData = async (payload) => {
        setIsLoading(true)
        try {
            setErrors({})
            const response = await Server.get(`/todos/?_status=${payload.status}&fdate=${fdate}&tdate=${tdate}${category_id ? `&category_id=${payload.category_id}` : ""}`)
            setTodos(response?.data)
            setIsLoading(false)
        } catch (error) {
            setIsLoading(false)
            if(error?.response?.status === 401){
                localStorage.removeItem('user')
                navigate('/login')
            }else if (error?.response?.status === 422) {
                setErrors({})
                error?.response?.data?.detail.map((err) => (
                    setErrors((prevState) => ({
                        ...prevState,
                        [err.loc[1]]: err.msg
                    }))
                ))
            }else if (error?.response?.status === 403){
                toast.error(error?.response?.data?.detail || 'Forbidden');
                navigate('/');
            }else {
                toast.error(error?.response?.data?.detail || 'Failed to load todos')
            }
        }
    }

    const handleClose = () => {
        if (isDeleting) return
        setShow(false)
    }
    const handleShow = (public_id, action = 'delete') => {
        setShow(true)
        setPublicID(public_id)
        setModalAction(action)
    };

    const handleDelete = async (public_id) => {
        setIsDeleting(true)
        try {
            const response = await Server.delete(`/todos/${public_id}`)
            if (response.status === 200){
                toast.success(response.data.detail)
                setTodos(prev => prev.filter((todo) => todo.public_id !== public_id))
            }else{
                toast.error(response?.data?.detail || 'Failed to delete todo')
            }
        } catch (error) {
            if(error?.response?.status === 401){
                navigate('/login')
            }else if (error?.response?.status === 403){
                toast.error(error?.response?.data?.detail || 'Forbidden');
                navigate('/');
            }else{
                toast.error(error?.response?.data?.detail || 'Failed to delete todo')
            }
        } finally {
            setIsDeleting(false)
            setShow(false)
        }
    }

    const handleComplete = async (public_id) => {
        setIsDeleting(true)
        try {
            const response = await Server.put(`/todos/${public_id}/complete`)
            toast.success(response.data.detail)
            const statusName = statusOptions.find(o => o.id === parseInt(formData.status))?.name || formData.status
            fetchData({status: statusName, category_id: category_id})
        } catch (error) {
            if(error?.response?.status === 401){
                navigate('/login')
            }else if (error?.response?.status === 403){
                toast.error(error?.response?.data?.detail || 'Forbidden');
                navigate('/');
            }else{
                toast.error(error?.response?.data?.detail || 'Failed to complete todo')
            }
        } finally {
            setIsDeleting(false)
            setShow(false)
        }
    }

    const handleConfirm = async (public_id) => {
        setIsDeleting(true)
        try {
            const response = await Server.post(`/todos/confirm/${public_id}`)
            toast.success(response.data.detail)
            const statusName = statusOptions.find(o => o.id === parseInt(formData.status))?.name || formData.status
            fetchData({status: statusName, category_id: category_id})
        } catch (error) {
            if(error?.response?.status === 401){
                navigate('/login')
            }else if (error?.response?.status === 403){
                toast.error(error?.response?.data?.detail || 'Forbidden');
                navigate('/');
            }else{
                toast.error(error?.response?.data?.detail || 'Failed to confirm todo')
            }
        } finally {
            setIsDeleting(false)
            setShow(false)
        }
    }

    const handleUndo = async (public_id) => {
        setIsDeleting(true)
        try {
            const response = await Server.put(`/todos/${public_id}/undo`)
            toast.success(response.data.detail)
            const statusName = statusOptions.find(o => o.id === parseInt(formData.status))?.name || formData.status
            fetchData({status: statusName, category_id: category_id})
        } catch (error) {
            if(error?.response?.status === 401){
                navigate('/login')
            }else if (error?.response?.status === 403){
                toast.error(error?.response?.data?.detail || 'Forbidden');
                navigate('/');
            }else{
                toast.error(error?.response?.data?.detail || 'Failed to undo todo')
            }
        } finally {
            setIsDeleting(false)
            setShow(false)
        }
    }

    const onChange = (e) => {
        const selectedOption = categoryOptions.find(option => option.value === e.target.value);
        setFormData((prevState) => ({
            ...prevState,
            [e.target.name]: e.target.value
        }))
    }

    const onSubmit = (e) => {
        e.preventDefault()

        const statusName = statusOptions[formData.status - 1]?.name || "Pending"
        fetchData({status: statusName, category_id})
        setTitle(`From ${fdate} To ${tdate} ${statusName} Todo list`)
    }

    const onGridReady=(params)=>{
        gridRef.current = params.api;
    }

    const onExportClick=()=>{
        if (gridRef.current) {
            gridRef.current.exportDataAsCsv({ fileName: `${title}.csv` });
        }
    }

    const defaultColDef = useMemo(() => {
        return {
            filterParams: {
                buttons: []
            }
        };
    }, []);

    const onFirstDataRendered = (params) => {
        params.api.autoSizeAllColumns();
    }

    const onGridSizeChanged = (params) => {
        params.api.autoSizeAllColumns();
    }

    if(isLoading){
        return <Spinner/>
    }

  return (
    <>
        <form onSubmit={onSubmit}>
            <div className="row">
                <div className="col-md-2">
                    <InputField
                        name="fdate"
                        type="date"
                        required="required"
                        value={fdate}
                        onChange={onChange}
                        error={errors.fdate}
                    />
                </div>
                <div className="col-md-2">
                    <InputField
                        name="tdate"
                        type="date"
                        required="required"
                        value={tdate}
                        onChange={onChange}
                        error={errors.tdate}
                    />
                </div>
                <div className="col-md-2">
                    <AsyncSelectField
                        name="category_id"
                        value={category_id}
                        onChange={onChange}
                        loadOptions={(query) => loadOptions('todo-categories', query, setCategoryOptions)}
                        options={categoryOptions}
                        placeholder="Category"
                        error={errors.category_id}
                    />
                </div>
                <div className="col-md-2">
                    <SelectField
                        name="status"
                        value={parseInt(status)}
                        options={statusOptions}
                        onChange={onChange}
                        error={errors.status}
                    />
                </div>
                <div className="col-md-2">
                    <input type="submit" className="btn btn-info mt-1" value="Apply Filter" style={{float: 'left'}}/>
                </div>
                <div className="col-md-1">
                    <button onClick={()=>onExportClick()} className="btn btn-success mb-2" style={{float: 'right'}}><FaFileExport/> &nbsp;Export</button>
                </div>
                <div className="col-md-1">
                    <Link to='/add-todo' className="btn btn-outline-info mb-2" style={{float: 'right'}}>Add Todo</Link>
                </div>
            </div>
            <br/>
        </form>
        <div className="card">
        <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                <h3 className="card-title">{title}</h3>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-12">
                        <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            suppressMenuHide={true}
                            defaultColDef={defaultColDef}
                            onFirstDataRendered={onFirstDataRendered}
                            onGridSizeChanged={onGridSizeChanged}

                            rowData={todos}
                            domLayout={"normal"}
                            rowSelection={"multiple"}

                            animateRows={true}

                            pagination={false}
                            onGridReady={onGridReady}

                            resizable={true}
                            enableCellTextSelection={true}
                            suppressCellFocus={true}

                            ref={gridRef}
                            columnDefs={columnDefs}
                        ></AgGridReact>{" "}
                    </div>
                    </div>
                </div>
        </div>
        </div>

        <Modal show={show} onHide={handleClose} backdrop={isDeleting ? 'static' : true}>
        <Modal.Header closeButton={!isDeleting}>
          <Modal.Title>
            {modalAction === 'delete' ? 'Delete' : modalAction === 'complete' ? 'Complete' : modalAction === 'confirm' ? 'Confirm' : 'Undo'} Todo
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to {modalAction} this todo?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isDeleting}>
            No
          </Button>
          <Button variant={modalAction === 'delete' ? "danger" : modalAction === 'undo' ? "warning" : "success"} disabled={isDeleting} onClick={() => {
                if (modalAction === 'delete') handleDelete(publicID);
                else if (modalAction === 'complete') handleComplete(publicID);
                else if (modalAction === 'confirm') handleConfirm(publicID);
                else if (modalAction === 'undo') handleUndo(publicID);
                }}>
            {isDeleting ? 'Processing…' : 'Yes'}
          </Button>
        </Modal.Footer>
      </Modal>
      <br/>
    </>
  )
}

export default Todos